import React from 'react'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {SET_FORCE_PASSWORD_CHANGE} from '../../../gql/mutations/userMutations'

type ForcePasswordChangeCardProps = {
  userId: string
  forcePasswordChange?: boolean
  onUpdated?: () => void
}

export const ForcePasswordChangeCard: React.FC<ForcePasswordChangeCardProps> = ({
  userId,
  forcePasswordChange = false,
  onUpdated,
}) => {
  const [enabled, setEnabled] = React.useState<boolean>(forcePasswordChange)
  const [setForcePasswordChange, {loading}] = useMutation(SET_FORCE_PASSWORD_CHANGE)

  React.useEffect(() => {
    setEnabled(Boolean(forcePasswordChange))
  }, [forcePasswordChange])

  const handleSave = async () => {
    try {
      const response = await setForcePasswordChange({
        variables: {
          userId: parseId(userId),
          enabled,
        },
      })

      const result = response.data?.setForcePasswordChangeMutation
      if (result?.success) {
        toast.success(
          enabled
            ? 'Force password change enabled for next login.'
            : 'Force password change disabled.'
        )
        onUpdated?.()
        return
      }

      const errorMessage = result?.errors?.[0]?.message || 'Unable to update this setting.'
      toast.error(errorMessage)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(`Error updating setting: ${message}`)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Login Security</h3>
        </div>
      </div>

      <div className='card-body border-top p-9'>
        <div className='form-check form-switch form-check-custom form-check-solid mb-6'>
          <input
            className='form-check-input'
            type='checkbox'
            id='force_password_change_next_login'
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label className='form-check-label fw-bold' htmlFor='force_password_change_next_login'>
            Force password change on next login
          </label>
        </div>

        <div className='text-gray-600 fs-7'>
          If enabled, this user must change their password before accessing the system.
        </div>
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button type='button' className='btn btn-primary' onClick={handleSave} disabled={loading}>
          {!loading && 'Save'}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
